import { FC, useEffect } from "react";
import classNames from "classnames";
import { Button, Checkbox, Input, InputNumber, LegacyForm, Modal, Select, Space } from "@payping/web-components";
import { unmaskStrToNum } from "@payping/utils/string";

import { useAppTranslation } from "@/utils/useAppTranslation";
import styles from "@/assets/scss/Shop/Invoice/scss/form.module.scss";
import { ConfigItemModalProps, Product } from "./type";
import { CuToman } from "@payping/icons";
import { numberFormatter } from "@payping/utilities-shared/number";

const ConfigItemModal: FC<ConfigItemModalProps> = ({ item, maxQuantity, onCancel, onOk }) => {
  const visible = !!item;
  const [formRef] = LegacyForm.useForm();
  const { t } = useAppTranslation();

  useEffect(() => {
    formRef.setFieldsValue({
      ...item,
      haveTax: Boolean(item.taxtionAmount),
    });
  }, [formRef, item]);

  const footer = (
    <Space>
      <Button onClick={onCancel}>{t("cancel")}</Button>
      <Button type="primary" onClick={formRef.submit}>
        {t("ok")}
      </Button>
    </Space>
  );

  return (
    <Modal open={visible} title={t("product.edit.title")} onCancel={onCancel} footer={footer}>
      <LegacyForm
        className={classNames(styles.form, styles.visible)}
        layout={"vertical"}
        onFinish={(values) => {
          const aa = {
            ...item,
            ...values,
            amount: Number(values.amount),
            defineAmountByUser: false,
            discountAmount: Number(values.discountAmount),
            haveTax: values?.haveTax ?? false,
            tax: values?.haveTax ?? false,
            taxtionAmount: values?.haveTax === false ? 0 : item.taxtionAmount,
            unlimited: false,
          };
          onOk(aa as Product);
        }}
        form={formRef}
      >
        <LegacyForm.Item
          label={t("product.itemTitle")}
          rules={[{ required: true }]}
          name={"title"}
          className="tw:mt-[10px]!"
        >
          <Input />
        </LegacyForm.Item>
        <LegacyForm.Item
          label={t("product.count")}
          rules={[
            { required: true },
            maxQuantity
              ? {
                  max: maxQuantity,
                  message: t("error.itemExceeded"),
                  min: 1,
                  transform: (value): number => unmaskStrToNum(value),
                  type: "number",
                }
              : {
                  message: t("error.minItemCount"),
                  min: 1,
                  transform: (value): number => unmaskStrToNum(value),
                  type: "number",
                },
          ]}
          name={"quantity"}
          className="tw:mt-[10px]!"
          initialValue={1}
        >
          <Input />
        </LegacyForm.Item>
        <LegacyForm.Item
          label={t("product.price")}
          rules={[{ required: true }]}
          name={"amount"}
          className="tw:mt-[10px]!"
          initialValue={0}
        >
          <InputNumber
            formatter={(value) => numberFormatter.format(value, { maxDecimals: 2 })}
            parser={(value) => numberFormatter.parse(value)}
            className="tw:w-full!"
            suffix={<CuToman width={24} height={24} />}
            inputMode="numeric"
            changeOnWheel={false}
            controls={false}
          />
        </LegacyForm.Item>
        <LegacyForm.Item name={"haveTax"} className="tw:mt-[10px]!" valuePropName="checked">
          <Checkbox>{t("tax")}</Checkbox>
        </LegacyForm.Item>
        <LegacyForm.Item shouldUpdate={() => true} label={"تخفیف"}>
          {({ getFieldValue }) => (
            <Space.Compact className={"tw:w-full"}>
              <LegacyForm.Item
                label={t("invoice.detail.discount")}
                noStyle
                name="discountAmount"
                initialValue={0}
                rules={[
                  getFieldValue("discountType") === 0
                    ? { max: 99, min: 0, transform: (value): number => unmaskStrToNum(value), type: "number" }
                    : { transform: (value): number => unmaskStrToNum(value), type: "number" },
                ]}
              >
                <InputNumber
                  formatter={(value) => numberFormatter.format(value, { maxDecimals: 0 })}
                  parser={(value) => numberFormatter.parse(value)}
                  className="tw:w-full!"
                  suffix={getFieldValue("discountType") === 0 ? "%" : <CuToman width="20px" height="20px" />}
                  inputMode="numeric"
                  changeOnWheel={false}
                  controls={false}
                />
              </LegacyForm.Item>
              <LegacyForm.Item noStyle name="discountType" initialValue={0}>
                <Select>
                  <Select.Option value={0}>{t("percent")}</Select.Option>
                  <Select.Option value={1}>{t("price")}</Select.Option>
                </Select>
              </LegacyForm.Item>
            </Space.Compact>
          )}
        </LegacyForm.Item>
      </LegacyForm>
    </Modal>
  );
};

export default ConfigItemModal;
